import * as React from 'react'
import { FC } from 'react'
import { getStyles } from './hooks/useStyles'
import {
  SimpleAccordion,
  SimpleAccordionDetails,
  SimpleAccordionSummary,
} from '@sales-center/components'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import Stack from '@mui/material/Stack'
import { ICompanySetup } from '@user/store/companySetup/typings'
import { useTranslation } from 'react-i18next'
import Box from '@mui/material/Box'
import { useTheme } from '@mui/material/styles'
import { ICompanyHeaderDisplaySettingsDTO } from '@user/store/companyHeaderDisplaySettings'
import {
  COMPANY_INFO_LABEL_WIDTH,
  COMPANY_INFO_VALUE_SM_WIDTH,
  COMPANY_INFO_VALUE_WIDTH,
  CompanyInfoRow,
} from './CompanyInfoRow'
import { useCompanyHeaderDisplaySettings } from './hooks/useCompanyHeaderDisplaySettings'

interface Props {
  companySetup: ICompanySetup
}

const displayText = (inputText: string | undefined) =>
  !!inputText && inputText.trim().length > 0 ? inputText : '-'

export const CompanyInfo: FC<Props> = (props) => {
  const { companySetup } = props
  const { t } = useTranslation()
  const styles = getStyles(useTheme())
  const { settings, disabled, handleToggle } = useCompanyHeaderDisplaySettings()

  const headerDisplay = (
    name: keyof ICompanyHeaderDisplaySettingsDTO,
    testIdSuffix: string,
  ) => ({
    name,
    checked: settings[name],
    disabled,
    onChange: handleToggle,
    testId: `company-header-display-${testIdSuffix}`,
  })

  return (
    <Box sx={styles.mainBranchLabel}>
      <img src="../static-assets/icons/companySetup.svg"></img>
      <SimpleAccordion square sx={styles.accordion}>
        <SimpleAccordionSummary expandIcon={<ExpandMoreIcon />}>
          {companySetup.id === ''
            ? 'Loading Company Info...'
            : companySetup.name}
        </SimpleAccordionSummary>
        <SimpleAccordionDetails>
          <Stack sx={styles.mainBranchDetail}>
            <Box
              sx={[
                {
                  pl: {
                    sm: `${COMPANY_INFO_LABEL_WIDTH + COMPANY_INFO_VALUE_SM_WIDTH}px`,
                    md: `${COMPANY_INFO_LABEL_WIDTH + COMPANY_INFO_VALUE_WIDTH}px`,
                  },
                },
                styles.label,
              ]}
            >
              {t('Display in Header')}
            </Box>
            <CompanyInfoRow
              label="Company Name "
              labelSx={styles.label}
              value={displayText(companySetup.name)}
              headerDisplay={headerDisplay('showCompanyName', 'company-name')}
            />
            <CompanyInfoRow
              label="Address "
              labelSx={styles.label}
              value={
                companySetup.address.address +
                ' ' +
                companySetup.address.address2
              }
              headerDisplay={headerDisplay('showAddress', 'address')}
            />
            <CompanyInfoRow
              label="City "
              labelSx={styles.label}
              value={displayText(companySetup.address.city)}
              headerDisplay={headerDisplay('showCity', 'city')}
            />
            <CompanyInfoRow
              label={t('state') + ' '}
              labelSx={styles.label}
              value={displayText(companySetup.address.state)}
              headerDisplay={headerDisplay('showState', 'state')}
            />
            <CompanyInfoRow
              label={t('postalCode') + ' '}
              labelSx={styles.label}
              value={displayText(companySetup.address.postalCode)}
              headerDisplay={headerDisplay('showPostalCode', 'postal-code')}
            />
            <CompanyInfoRow
              label="Phone "
              labelSx={styles.label}
              value={displayText(companySetup.phone)}
            />
            <CompanyInfoRow
              label="Time Zone "
              labelSx={styles.label}
              value={displayText(companySetup.timeZone)}
            />
            <CompanyInfoRow
              label="Email "
              labelSx={styles.label}
              value={displayText(companySetup.email)}
            />
          </Stack>
        </SimpleAccordionDetails>
      </SimpleAccordion>
    </Box>
  )
}
